/**
 * Resolving conditional text (D55) — the ONE describer.
 *
 * @remarks
 * `ConditionalText` is authored either as a plain string or as ordered
 * variants over a mandatory fallback (`content-schema/conditional-text.ts`).
 * Resolution is first-match-wins: the most specific reading is authored first
 * and the fallback speaks when nothing else does.
 *
 * The load-bearing claim of this module is that there is exactly one describer
 * and everything else is spelling. `openable.openDescription` /
 * `closedDescription` — a second mechanism from D50 — were desugared HERE
 * into two variants from D55 until D159 retired them: sugar that desugars
 * inside the engine is sugar three other readers still have to know (the
 * audit roster, the coverage gate, the schema's own refinement), which is
 * D158's one-seam rule failed. The 0.2.0 → 0.3.0 migration wrote the two
 * variants into the documents; the describer reads variants and nothing else.
 *
 * That matters beyond tidiness. Per-state description FIELDS do not compose:
 * the moment a thing has two such properties — a cabinet that is open-or-shut
 * and also running-or-dead — there are four readings and one description slot,
 * and any precedence rule silently strands three of them. Variants compose by
 * construction, because a condition can name both.
 *
 * @packageDocumentation
 */

import type {
	ConditionalText,
	DescriptionFragment,
	ItemDefinition,
	NarrativeLine,
	RoomDefinition,
	RoomDescriptionForm,
	RuntimeState,
} from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from '../conditions/command-in-hand.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { firstMatchingVariant } from '../conditions/first-matching-variant.js';
import { withCountersPrinted } from '../entities/counter-state.js';

/**
 * A narrative line whose reading has been CHOSEN — `text` is a plain string,
 * never the conditional form (R15, Calder's §5.2). Assignable to
 * {@link NarrativeLine} (a plain string is valid conditional text), so
 * resolved lines flow through `stampNarrativeLines` unchanged and resolution
 * is idempotent. `ActionRejection.narratives` carries THIS type: the seam
 * hands out what the transcript will say, and a consumer that renders one
 * directly can no longer regress into showing an author's variants object.
 */
export type ResolvedNarrativeLine = { readonly text: string } & NarrativeLine;

/**
 * Resolve one piece of authored conditional text against the live world.
 *
 * @remarks
 * The one resolution path. `leadingVariants` is how a SHORTHAND folds in:
 * callers translate their sugar into ordinary variants and hand them over to be
 * resolved by the same first-match walk as everything else, rather than
 * branching on whether sugar was present. That is what keeps "one describer"
 * literally true — there is no second place a description can be chosen, and no
 * arm reachable only by one spelling.
 *
 * @param text - The authored string, or its conditional form
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param commandInHand - D219: the command being answered, when the caller is answering one
 * @returns The text that should speak, its `{counter:…}` tokens printed (D244)
 */
export const resolveConditionalText = (
	text: ConditionalText,
	bundle: EngineGameBundle,
	state: RuntimeState,
	commandInHand?: CommandInHand,
): string =>
	// D244: the chosen reading prints its counters here, at the one seam every
	// spoken line, description, fragment and question passes through — so a
	// number in prose is the number at the moment the line is said.
	withCountersPrinted(
		typeof text === 'string'
			? text
			: (firstMatchingVariant(text.variants, (variant) => variant.condition, bundle, state, commandInHand)?.text ?? text.fallback),
		bundle,
		state,
	);

/**
 * Resolve every line's reading against the live world (R15).
 *
 * @remarks
 * The rejection factories call this at CREATION, so a rejection's narratives
 * are already what the transcript would say (Calder's §5.2 ruling: resolving
 * at the seam beats a doc comment asking consumers to be careful). State is
 * optional because narrowing runs from tooling and tests without a world;
 * absent one, the FALLBACK reading is chosen — the same safe direction as
 * `canOpenByHand`'s "a caller with no world must not be handed a bypass".
 *
 * @param lines - The authored lines, readings unchosen
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param commandInHand - D219: the command being answered, when the caller is answering one
 * @returns The same lines with their readings chosen
 */
export const resolveNarrativeLines = (
	lines: readonly NarrativeLine[],
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	commandInHand?: CommandInHand,
): readonly ResolvedNarrativeLine[] =>
	lines.map((line) => ({
		...line,
		text:
			state === undefined
				? typeof line.text === 'string'
					? line.text
					: line.text.fallback
				: resolveConditionalText(line.text, bundle, state, commandInHand),
	}));

/**
 * What a thing looks like RIGHT NOW.
 *
 * @remarks
 * The single describer for items, shared by `inspect` and the door verbs so
 * that opening a thing and then examining it can never disagree — the defect
 * D50 fixed for doors, now unable to recur for any property, because there is
 * no second place where a description could be chosen.
 *
 * One line, and no spelling to branch on (D159): a thing with a door reads
 * differently open because its description says so, under `container_open`.
 *
 * D232: an actor's page has the same two fields and reads through the same
 * describer — `examine the woman` is the player seeing her, in the
 * environment's channel, with her fragments appended while they hold — so
 * the parameter is the page, not the item.
 *
 * @param item - The item or actor being described — its description and fragments
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The description for its current state
 */
export const describeItemInState = (item: Pick<ItemDefinition, 'description' | 'descriptionFragments'>, bundle: EngineGameBundle, state: RuntimeState): string =>
	withFragments(
		resolveConditionalText(item.description, bundle, state),
		item.descriptionFragments,
		bundle,
		state,
	);

/**
 * What a room reads like RIGHT NOW, in the form the moment calls for — the
 * long description or the brief re-orientation (D82) — with its fragments
 * appended (D140). The single describer for rooms: `look`, first entry, and
 * re-entry all read through here, so the three cannot disagree about what a
 * room says. A fragment that names a form (D237) joins that form alone — the
 * sentence that finishes the page, which the re-orientation never said.
 *
 * @param room - The room being described
 * @param form - Which authored form the moment reads
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The description for its current state
 */
export const describeRoomInState = (
	room: RoomDefinition,
	form: RoomDescriptionForm,
	bundle: EngineGameBundle,
	state: RuntimeState,
): string =>
	withFragments(
		resolveConditionalText(form === 'brief' ? (room.briefDescription ?? room.description) : room.description, bundle, state),
		room.descriptionFragments.filter((fragment) => fragment.form === undefined || fragment.form === form),
		bundle,
		state,
	);

/**
 * Append every fragment whose condition holds, in declared order, one space
 * after the base — a fragment is a sentence that is true IN ADDITION (D140),
 * and it is appended to whichever form was read, because "the gas is here"
 * is true of the room however briefly the room was described (a room's
 * fragment that names one form arrives here only when that form was read —
 * D237, filtered by the caller).
 *
 * @param base - The resolved description
 * @param fragments - The authored fragments, in order
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The description with its live fragments
 */
const withFragments = (
	base: string,
	fragments: readonly DescriptionFragment[],
	bundle: EngineGameBundle,
	state: RuntimeState,
): string =>
	[
		base,
		...fragments
			.filter((fragment) => evaluateCondition(fragment.when, bundle, state))
			.map((fragment) => resolveConditionalText(fragment.text, bundle, state)),
	].join(' ');
